import React, { useState } from 'react';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedImage from './AnimatedImage';

interface ImageCarouselProps {
  images: string[] | Array<{ imageUrl?: string; imageId?: string; image?: string; [key: string]: any }>;
  height?: number | string;
  autoPlay?: boolean;
  interval?: number;
  showThumbnails?: boolean;
  showNavigation?: boolean;
  showDots?: boolean;
  onImageClick?: () => void;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  height = 400,
  autoPlay = false,
  interval = 5000,
  showThumbnails = true,
  showNavigation = true,
  showDots = true,
  onImageClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Normalize images to string array
  const imageUrls = React.useMemo(() => {
    if (!images || images.length === 0) return [];
    return images.map((img) => {
      if (typeof img === 'string') return img;
      // Handle Image type with imageUrl property
      if (img && typeof img === 'object') {
        return (img as any).imageUrl || (img as any).image || '';
      }
      return '';
    }).filter(Boolean);
  }, [images]);

  React.useEffect(() => {
    if (!autoPlay || imageUrls.length <= 1) return;

    const timer = setInterval(() => {
      setDirection('right');
      setCurrentIndex((prev) => (prev + 1) % imageUrls.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, imageUrls.length]);

  const goToPrevious = () => {
    setDirection('left');
    setCurrentIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  };

  const goToNext = () => {
    setDirection('right');
    setCurrentIndex((prev) => (prev + 1) % imageUrls.length);
  };

  const goToSlide = (index: number) => {
    setDirection(index > currentIndex ? 'right' : 'left');
    setCurrentIndex(index);
  };

  if (imageUrls.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.200',
        }}
      >
        No images available
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Main Image */}
      <Box
        sx={{
          position: 'relative',
          height,
          width: '100%',
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            initial={{ opacity: 0, x: direction === 'right' ? 100 : -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction === 'right' ? -100 : 100 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', height: '100%' }}
          >
            <Box
              onClick={onImageClick}
              sx={{ cursor: onImageClick ? 'pointer' : 'default', width: '100%', height: '100%' }}
            >
              <AnimatedImage
                src={imageUrls[currentIndex]}
                alt={`Image ${currentIndex + 1}`}
                height="100%"
                objectFit="cover"
                lazy={false}
              />
            </Box>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        {showNavigation && imageUrls.length > 1 && (
          <>
            <IconButton
              onClick={goToPrevious}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                zIndex: 2,
              }}
              component={motion.button}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={goToNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                zIndex: 2,
              }}
              component={motion.button}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}

        {/* Indicators */}
        {showDots && imageUrls.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              zIndex: 2,
            }}
          >
            {imageUrls.map((_, index) => (
              <Box
                key={index}
                onClick={() => goToSlide(index)}
                component={motion.div}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                sx={{
                  width: currentIndex === index ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: currentIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'width 0.3s',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Thumbnails */}
      {showThumbnails && imageUrls.length > 1 && !isMobile && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt: 2,
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': {
              height: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.400',
              borderRadius: 2,
            },
          }}
        >
          {imageUrls.map((image, index) => (
            <Box
              key={index}
              onClick={() => goToSlide(index)}
              component={motion.div}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              sx={{
                minWidth: 80,
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                border: currentIndex === index ? '3px solid' : '1px solid',
                borderColor: currentIndex === index ? 'primary.main' : 'divider',
                opacity: currentIndex === index ? 1 : 0.7,
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              <AnimatedImage
                src={image}
                alt={`Thumbnail ${index + 1}`}
                height={80}
                width={80}
                objectFit="cover"
                lazy={false}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ImageCarousel;
